import "./App.css";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ChatLayout from "./pages/ChatLayout";
import Composer from "./pages/Composer";
import CheckoutView from "./components/CheckoutView";
import BottomSheet from "./ui/BottomSheet";
import ProductSheet from "./components/ui/ProductSheet";
import PresetModal from "./components/ui/PresetModal";
import DeliveryModal from "./components/ui/DeliveryModal";
import CatalogSheet from "./ui/CatalogSheet";

import { useChatSession } from "./chat/useChatSession";
import { isFavoriteProductByIds } from "./app/app.helpers";
import { createUiHandlers } from "./app/app.handlers";
import {
  bindAuthChanged,
  restoreAfterCheckoutReturn,
} from "./app/app.effects";
import { getMyAccountData } from "./api/api";

export default function App() {
  const { t } = useTranslation("cart");

  const {
    chatId,
    loadChat,
    startNewChat,
    resetSession,

    screen,
    setScreen,

    messagesWithTyping,
    ctas,
    loading,

    cartItems,
    cartSummary,

    send,
    onCtaClick,
    onAction,
    appendOrderConfirmation,
  } = useChatSession();

  const [preset, setPreset] = useState("dine_in");
  const [presetModalOpen, setPresetModalOpen] = useState(false);

  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const [productOpen, setProductOpen] = useState(false);
  const [sheetProduct, setSheetProduct] = useState(null);

  const [catalogOpen, setCatalogOpen] = useState(false);

  const [awaitingCheckout, setAwaitingCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const location = useLocation();
  const nav = useNavigate();
  const { chatId: routeChatId } = useParams();

  const ignoreFavoritesRef = useRef(false);
  const loadedChatRef = useRef(null);
  const pendingNewChatRef = useRef(false);
  const checkoutRestoreRef = useRef(false);

  useEffect(() => {
    const stateChatId = location.state?.openChatId;
    const nextChatId = String(stateChatId || routeChatId || "").trim();

    if (!nextChatId) return;
    if (loadedChatRef.current === nextChatId) return;

    loadedChatRef.current = nextChatId;

    (async () => {
      try {
        await loadChat(nextChatId);
        setScreen("chat");
      } catch (e) {
        console.error("open chat from route/state error:", e);
      } finally {
        if (stateChatId) {
          nav(`/chat/${nextChatId}`, { replace: true });
        }
      }
    })();
  }, [location.key, routeChatId, loadChat, nav, setScreen]);

  useEffect(() => {
    if (!pendingNewChatRef.current) return;
    if (location.pathname !== "/") return;

    pendingNewChatRef.current = false;
    loadedChatRef.current = null;
    startNewChat?.();
  }, [location.pathname, startNewChat]);

  useEffect(() => {
    const unsub = bindAuthChanged({
      resetSession,
      setIsLoggedIn,
      setAuthReady,
      setFavoriteIds,
      setFavoriteItems,
      setCartOpen,
      setProductOpen,
      setSheetProduct,
      setCatalogOpen,
      setPresetModalOpen,
      setDeliveryOpen,
      setAwaitingCheckout,
    });

    return () => unsub();
  }, [resetSession]);

  const handlers = createUiHandlers({
    chatId,
    cartItems,
    cartSummary,
    preset,
    checkoutLoading,
    setCheckoutLoading,
    setCartOpen,
    setProductOpen,
    setSheetProduct,
    setCatalogOpen,
    setScreen,
    setPresetModalOpen,
    setAwaitingCheckout,
    setDeliveryOpen,
    onAction,
    onCtaClick,
    startNewChat,
    loadChat,
    send,
    ctas,
    loading,
    favoriteIds,
    setFavoriteIds,
    favoriteItems,
    setFavoriteItems,
    authReady,
    isLoggedIn,
    cartOpen,
    productOpen,
    catalogOpen,
  });

  useEffect(() => {
    restoreAfterCheckoutReturn({
      cartItemsLength: cartItems.length,
      setCheckoutLoading,
      setPresetModalOpen,
      setDeliveryOpen,
      setAwaitingCheckout,
      setScreen,
      setCartOpen,
    });

    function onPageShow() {
      restoreAfterCheckoutReturn({
        cartItemsLength: cartItems.length,
        setCheckoutLoading,
        setPresetModalOpen,
        setDeliveryOpen,
        setAwaitingCheckout,
        setScreen,
        setCartOpen,
      });
    }

    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [cartItems.length, setScreen]);

  useEffect(() => {
  async function restorePaidOrderToChat() {
    const isCheckoutSuccess = !!location.state?.checkoutSuccess;

    if (!isCheckoutSuccess || checkoutRestoreRef.current) return;

    checkoutRestoreRef.current = true;

    try {
      const targetChatId =
        location.state?.openChatId ||
        sessionStorage.getItem("checkout_chat_id");

      if (targetChatId) {
        await loadChat(targetChatId);
      }

      setScreen("chat");

      const data = await getMyAccountData();
      const purchases = Array.isArray(data?.purchases) ? data.purchases : [];

      const paidOrders = purchases.filter(
        (o) => String(o?.status || "").toLowerCase() === "paid"
      );

      const latestOrder = paidOrders.sort((a, b) => {
        const da = new Date(a?.date || a?.created_at || 0).getTime();
        const db = new Date(b?.date || b?.created_at || 0).getTime();
        return db - da;
      })[0];

      if (latestOrder && targetChatId) {
  await appendOrderConfirmation(latestOrder, targetChatId);
}
    } catch (e) {
      console.error("restorePaidOrderToChat error:", e);
    } finally {
      sessionStorage.removeItem("checkout_return_pending");
      sessionStorage.removeItem("checkout_chat_id");
    }
  }

  restorePaidOrderToChat();
}, [location.state, loadChat, setScreen, appendOrderConfirmation]);

  useEffect(() => {
    if (!authReady) return;

    ignoreFavoritesRef.current = false;
    handlers.loadFavoritesSafe({ ignoreRef: ignoreFavoritesRef });

    return () => {
      ignoreFavoritesRef.current = true;
    };
  }, [authReady, isLoggedIn]);

  const isFavorite = useMemo(
    () => isFavoriteProductByIds(sheetProduct, favoriteIds),
    [sheetProduct, favoriteIds]
  );

  const checkoutProps = handlers.buildCheckoutProps();

  function handleStartFreshChat() {
    loadedChatRef.current = null;

    if (location.pathname !== "/") {
      pendingNewChatRef.current = true;
      nav("/");
      return;
    }

    startNewChat?.();
  }

  if (screen === "checkout") {
    return (
      <CheckoutView
        {...checkoutProps}
        onBack={handlers.handleCheckoutBack}
      />
    );
  }

  return (
    <>
      <ChatLayout
        chatId={chatId}
        messages={messagesWithTyping}
        onBlockAction={handlers.handleAction}
        onMessageAction={handlers.handleMessageAction}
        onOpenChat={(id) => {
          if (!id) return;
          nav(`/chat/${id}`);
        }}
        onNewChat={handleStartFreshChat}
        onLogoClick={handleStartFreshChat}
        overlayOpen={
          catalogOpen ||
          productOpen ||
          cartOpen ||
          presetModalOpen ||
          deliveryOpen
        }
        favoriteItems={favoriteItems}
        onOpenProduct={handlers.handleOpenProduct}
        onRemoveFavorite={handlers.handleToggleFavorite}
        footer={
          <Composer
            ctas={ctas}
            loading={loading}
            onSend={send}
            onCtaClick={handlers.handleCtaClick}
            cartCount={cartSummary.itemsCount}
            onCartClick={handlers.openCart}
            onGridClick={handlers.handleOpenCatalog}
            cartOverlay={(productOpen || catalogOpen) && !cartOpen}
          />
        }
      />

      <CatalogSheet
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        topOffset={60}
        onOpenProduct={(p) => {
          if (!p) return;
          setCatalogOpen(false);
          setSheetProduct(p);
          setProductOpen(true);
        }}
        onAction={handlers.handleAction}
      />

      <PresetModal
        open={presetModalOpen}
        value={preset}
        onClose={() => {
          setPresetModalOpen(false);
          setAwaitingCheckout(false);
        }}
        onConfirm={(nextPreset) => {
          setPreset(nextPreset);
          setPresetModalOpen(false);

          if (!awaitingCheckout) return;

          if (nextPreset === "delivery") {
            setDeliveryOpen(true);
            return;
          }

          setAwaitingCheckout(false);
          handlers.handleCheckoutToOdoo();
        }}
      />

      <DeliveryModal
        open={deliveryOpen}
        onClose={() => {
          setDeliveryOpen(false);
          setAwaitingCheckout(false);
        }}
        onSaved={() => {
          setDeliveryOpen(false);

          if (awaitingCheckout) {
            setAwaitingCheckout(false);
            handlers.handleCheckoutToOdoo();
          }
        }}
      />

      <BottomSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        title={t("my_cart")}
        topOffset={60}
      >
        <CheckoutView {...checkoutProps} />
      </BottomSheet>

      <ProductSheet
        open={productOpen}
        onClose={() => {
          setProductOpen(false);
          setSheetProduct(null);
        }}
        product={sheetProduct}
        topOffset={60}
        onAddToCart={handlers.handleProductAddToCart}
        onOpenProduct={handlers.handleOpenProduct}
        onToggleFavorite={handlers.handleToggleFavorite}
        isFavorite={isFavorite}
      />
    </>
  );
}